import { S3_BASE_URL } from 'constants/static';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  customerState,
  isAuthenticatedState,
  isLoginFormOpenState,
  shopDetailState,
  shopState,
} from 'store/atoms';
import { BaseButton } from './BaseButton';
import { BaseLine } from './BaseLine';
import { countryOptions } from 'constants/countryOption';
import { useTranslation } from 'i18n';
import {
  Overlay,
  OverlayInner,
  MenuContainer,
  MenuHeader,
  MenuCustomerBlock,
  MenuNameBlock,
  MenuPhoneBlock,
  MenuContent,
  MenuItem,
  MenuIcon,
  MenuText,
  SignOutButton,
} from './SideMenu.styles';

interface SideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SideMenu = ({ isOpen, onClose }: SideMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const customer = useRecoilValue(customerState);
  const shop: any = useRecoilValue(shopState);
  const shopDetail: any = useRecoilValue(shopDetailState);
  const isAuthenticated = useRecoilValue(isAuthenticatedState);
  const setIsLoginFormOpen = useSetRecoilState(isLoginFormOpenState);
  const isPaymentUsed = shopDetail?.solutionConfig?.global_config?.isGeneralPaymentUsed;

  // 사이드바가 열릴 때 body 스크롤 비활성화, 닫힐 때 활성화
  useEffect(() => {
    if (isOpen) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // 스크롤 위치 복원
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // 컴포넌트 언마운트 시 스타일 초기화
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const menuItems = [
    {
      id: 'home',
      text: t('nav.home'),
      icon: `${S3_BASE_URL}/main_home_Icon.svg`,
      path: '/',
    },
    {
      id: 'my-booking',
      text: t('nav.my_booking'),
      icon: `${S3_BASE_URL}/my_booking_icon.svg`,
      path: '/my/booking',
    },
    {
      id: 'my-membership',
      text: t('nav.my_membership'),
      icon: `${S3_BASE_URL}/my_membership_icon.svg`,
      path: '/my/membership',
    },
    {
      id: 'card-management',
      text: t('nav.card_management'),
      icon: `${S3_BASE_URL}/card_management_icon.svg`,
      path: '/my/cards',
    },
    // {
    //   id: 'my-profile',
    //   text: 'My profile',
    //   icon: `${S3_BASE_URL}/my_profile_icon.svg`,
    //   path: '/my/profile',
    // },
  ];

  const handleMenuClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const logout = () => {
    // 로컬스토리지 완전 초기화
    localStorage.clear();
    onClose();
    navigate('/?shop=' + shop?.shopName);
    window.location.reload(); // 페이지 새로고침
  };

  const goToLoginPage = () => {
    setIsLoginFormOpen(true);
  };

  const translateCountryCode = (countryCode: string) => {
    return countryOptions.find((option) => option.value === countryCode)?.flag;
  };

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <OverlayInner onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
        <MenuContainer $isOpen={isOpen}>
          {isAuthenticated && localStorage.getItem('customer_token') ? (
            <MenuCustomerBlock>
              <MenuNameBlock>{customer?.customerInfo?.name}</MenuNameBlock>
              <MenuPhoneBlock>
                {translateCountryCode(shopDetail?.solutionConfig.global_config.countryCode)}{' '}
                {customer?.customerInfo?.phone}
                <SignOutButton onClick={() => logout()}>
                  {t('nav.sign_out')}
                  <img src={`${S3_BASE_URL}/logout.svg`} alt="logout_icon" width={16} height={16} />
                </SignOutButton>
              </MenuPhoneBlock>
            </MenuCustomerBlock>
          ) : (
            <MenuHeader>
              <BaseButton text={t('home.signin.button')} onClick={goToLoginPage} variant="primary" size="large" />
              <BaseLine margin="15px 0" />
            </MenuHeader>
          )}

          <MenuContent>
            {menuItems
              .filter((item) => (isPaymentUsed ? true : item.id !== 'card-management'))
              .map((item) => (
                <MenuItem key={item.id} onClick={() => handleMenuClick(item.path)}>
                  <MenuIcon>
                    <img src={item.icon} alt="menu_icon" />
                  </MenuIcon>
                  <MenuText>{item.text}</MenuText>

                  <img
                    src={`${S3_BASE_URL}/right_gray_arrow.svg`}
                    alt="right_gray_arrow_icon"
                    width={24}
                    height={24}
                  />
                </MenuItem>
              ))}
          </MenuContent>
        </MenuContainer>
      </OverlayInner>
    </Overlay>
  );
};
